import NitroService from "../../../utils/nitro.service";
import {
  IFavoriteLocationExtension,
  IFavoriteSetExtension,
  ModeValueRow,
  MonthHalf,
} from "../../../types/IFavoriteOrigins";
import { extendFavorites } from "../../../utils/extendFavorites";

type PriceInputStateParams = {
  origin: IFavoriteLocationExtension;
  modeRow: ModeValueRow;
  month: string;
  half: MonthHalf;
  dragHoverPriceKey: string | null;
  draftPrices: Record<string, string>;
  draftPriceShowAs: Record<string, string>;
  currentShowAs: string | null;
  isNewlyAddedOrigin?: boolean;
  getPriceInputKey: (
    origin: IFavoriteLocationExtension,
    modeKey: string,
    month: string,
    half: MonthHalf,
  ) => string;
};

export const getPriceInputState = ({
  origin,
  modeRow,
  month,
  half,
  dragHoverPriceKey,
  draftPrices,
  draftPriceShowAs,
  currentShowAs,
  isNewlyAddedOrigin = false,
  getPriceInputKey,
}: PriceInputStateParams) => {
  const priceKey = getPriceInputKey(origin, modeRow.key, month, half);
  const isDragHover = dragHoverPriceKey === priceKey;
  const previousValue = modeRow.value;
  const previousShowAs = modeRow.showAs ?? "PRICE";
  const hasExistingValue =
    previousValue !== null && previousValue !== undefined;
  const hasExistingState =
    hasExistingValue ||
    previousShowAs === "DISCUSS" ||
    previousShowAs === "NO_OFFER";
  const isEdited =
    (draftPrices[priceKey] !== undefined ||
      draftPriceShowAs[priceKey] !== undefined) &&
    (!isNewlyAddedOrigin || hasExistingState);
  const isDiscussOrNoOffer =
    currentShowAs === "DISCUSS" || currentShowAs === "NO_OFFER";
  let inputStateClass = "";
  if (isDragHover) inputStateClass += " dom-price-input--drag-hover";
  if (isEdited) inputStateClass += " dom-price-input--edited";
  if (isDiscussOrNoOffer && isEdited) {
    inputStateClass += " dom-price-input--highlight";
  }

  return {
    priceKey,
    inputStateClass,
    isReadOnly: isDiscussOrNoOffer,
  };
};

type SaveAndRefreshFavoriteOriginPricesOptions = {
  onLoadingChange?: (isLoading: boolean) => void;
  onError?: (error: Error) => void;
};

const toSaveAndRefreshError = (error: unknown): Error => {
  if (error instanceof Error) {
    return new Error(
      `Failed to save and refresh favorite origin prices: ${error.message}`,
    );
  }

  return new Error("Failed to save and refresh favorite origin prices.");
};

export const saveAndRefreshFavoriteOriginPrices = async (
  marketer: string,
  product: string,
  favorites: IFavoriteLocationExtension[],
  options?: SaveAndRefreshFavoriteOriginPricesOptions,
): Promise<IFavoriteSetExtension[]> => {
  options?.onLoadingChange?.(true);

  try {
    const normalized = await NitroService.getFavoritesByMarketer({ marketer });
    const favoriteSet = normalized
      .flatMap((entry) => entry.favoriteSets || [])
      .find((set) => set.product === product);

    const currentFavoriteLocations = favoriteSet?.favoriteLocations || [];
    const currentByKey = new Map<string, { id: string }>();

    currentFavoriteLocations.forEach((location) => {
      const rowKey = `${location.location.id}|${
        location.competitor ? "COMPETITOR" : "KOCH"
      }`;
      currentByKey.set(rowKey, { id: location.id });
    });

    const desiredByKey = new Map<string, IFavoriteLocationExtension>();
    favorites.forEach((favorite) => {
      const rowKey = `${favorite.location.id}|${
        favorite.competitor ? "COMPETITOR" : "KOCH"
      }`;
      desiredByKey.set(rowKey, favorite);
    });

    const createResults = new Map<string, string>();

    const toCreate = favorites
      .map((favorite, index) => ({ favorite, index }))
      .filter(({ favorite }) => {
        const rowKey = `${favorite.location.id}|${
          favorite.competitor ? "COMPETITOR" : "KOCH"
        }`;
        return !currentByKey.has(rowKey);
      });

    const createResponses = await Promise.all(
      toCreate.map(({ favorite, index }) =>
        NitroService.createFavorite({
          marketer,
          product,
          locationId: favorite.location.id,
          competitor: favorite.competitor,
          order: index,
        }),
      ),
    );

    toCreate.forEach(({ favorite }, i) => {
      const rowKey = `${favorite.location.id}|${
        favorite.competitor ? "COMPETITOR" : "KOCH"
      }`;
      const created = createResponses[i][0];
      if (created?.favLocId) {
        createResults.set(rowKey, created.favLocId);
      }
    });

    const toDelete = Array.from(currentByKey.entries()).filter(
      ([rowKey]) => !desiredByKey.has(rowKey),
    );

    await Promise.all(
      toDelete.map(([, existing]) =>
        NitroService.deleteFavorite({
          favSetId: favoriteSet?.id || `${marketer}|${product}`,
          favLocId: existing.id,
        }),
      ),
    );

    const orderedFavLocMaps = favorites
      .map((favorite, order) => {
        const rowKey = `${favorite.location.id}|${
          favorite.competitor ? "COMPETITOR" : "KOCH"
        }`;
        const currentId = currentByKey.get(rowKey)?.id;
        const createdId = createResults.get(rowKey);
        const id = currentId || createdId || (favorite as { id?: string }).id;
        if (!id) return null;
        return { id, order };
      })
      .filter(Boolean) as Array<{ id: string; order: number }>;

    if (orderedFavLocMaps.length > 0) {
      await NitroService.reorderFavorites({
        favSetId: favoriteSet?.id || `${marketer}|${product}`,
        orderedFavLocMaps,
      });
    }

    const result = await NitroService.getFavoritesByMarketer({
      marketer,
    });
    const extendedFavorites = extendFavorites(result[0].favoriteSets);
    return extendedFavorites;
  } catch (error) {
    const wrappedError = toSaveAndRefreshError(error);
    options?.onError?.(wrappedError);
    throw wrappedError;
  } finally {
    options?.onLoadingChange?.(false);
  }
};

const normalizeOutboundModes = (rawModes: string[]): string[] =>
  rawModes
    .map((mode) => String(mode).trim().toUpperCase())
    .filter((mode) => mode === "TRUCK" || mode === "RAIL");

export const createResolveOutboundModes = (locationId: string) => {
  let fetchedDetailModes: string[] | null = null;

  return async () => {
    if (fetchedDetailModes === null) {
      try {
        const details = await NitroService.getMultiLocationDetails2({
          ids: [locationId],
        });
        const firstDetail = details?.[0];
        const detailModes = firstDetail ? firstDetail.outboundModes : [];
        fetchedDetailModes = normalizeOutboundModes(detailModes);
      } catch {
        fetchedDetailModes = [];
      }
    }

    return fetchedDetailModes;
  };
};
